<template>
  <figure
    data-id="responsive-image"
    :class="['h-auto', getAspectRatio]"
  >
    <FocalImage
      :image="imageData"
      :srcset="srcset || undefined"
      :sizes="sizes || undefined"
      img-class="w-full h-full"
    />
  </figure>
</template>

<script lang="ts" setup>
import type { AspectRatio } from '../../composables/useAspectRatio';
import type { ImageWithFocalPoint } from '@/ts/types/focal-point';
import { computed, PropType } from 'vue';
import { useAspectRatio } from '../../composables/useAspectRatio';

/**
 * ResponsiveImage component props.
 *
 * @prop {string} src - The source URL of the image. Required.
 * @prop {string} alt - The alternative text for the image, used for accessibility. Required.
 * @prop {AspectRatio} [aspectRatio='auto'] - The aspect ratio of the image (e.g., '16/9', '4/3', or 'auto').
 * @prop {string|number|null} [width=null] - The width of the image. Can be a string (e.g., '100%') or a number (pixels).
 * @prop {string|number|null} [height=null] - The height of the image. Can be a string (e.g., '100%') or a number (pixels).
 * @prop {object} [focalPoint=null] - Optional focal point data from Wagtail.
 */
const props = defineProps({
  src: {
    type: String,
    required: true
  },
  alt: {
    type: String,
    required: true
  },
  aspectRatio: {
    type: String as PropType<AspectRatio>,
    default: 'auto'
  },
  width: {
    type: [String, Number],
    default: null
  },
  height: {
    type: [String, Number],
    default: null
  },
  focalPoint: {
    type: Object as PropType<{ centroid_x: number; centroid_y: number, width: number; height: number }>,
    default: null
  },
  srcset: {
    type: String,
    default: null
  },
  sizes: {
    type: String,
    default: null
  }
});

const { getAspectRatio } = useAspectRatio(() => props.aspectRatio);

/** Convert props to ImageWithFocalPoint format */
const imageData = computed((): ImageWithFocalPoint | { url: string; alt: string } => {
  const base = {
    url: props.src,
    alt: props.alt,
    title: props.alt,
  };

  if (props.focalPoint && props.width && props.height) {
    return {
      ...base,
      width: Number(props.width),
      height: Number(props.height),
      focal_point: props.focalPoint,
    };
  }

  return base;
});
</script>
